import { type CSSProperties } from 'typestyle/lib/types';
import { type SxProps } from '@mui/material';
import StyleConstants from '../../core/styles.constants';

export const headerMargin: CSSProperties = {
  marginBottom: 16,
};

export const bottomMargin: CSSProperties = {
  marginBottom: 24,
  paddingBottom: 24,
};

export const emailInputContainer: CSSProperties = {
  marginLeft: 8,
  marginRight: 8,
};

// Neither react-tooltip nor react-modal sets a z-index of its own, so stacking fell back to DOM
// order — and react-modal portals into document.body while the app's single global <Tooltip> lives
// inside #root. That put every modal on top of the tooltip, making tooltips inside a modal (e.g.
// the threshold hints in the room edit dialog) render behind it and appear broken. #root only gets
// width/height from csstips' setupPage, so it creates no stacking context and an explicit z-index
// here reliably lifts the tooltip above modals app-wide. Sits above the highest value used
// elsewhere in the app (1000).
export const tooltip: CSSProperties = {
  fontSize: StyleConstants.SECONDARY_FONT_SIZE,
  fontWeight: StyleConstants.CARD_BODY_FONT_WEIGHT,
  width: 250,
  zIndex: 10000,
};

export const tooltipIcon: CSSProperties = {
  cursor: 'pointer',
  color: StyleConstants.GREY_COLOR,
  height: 18,
  width: 18,
};

export const tooltipIconSX: SxProps = {
  cursor: 'pointer',
  color: StyleConstants.GREY_COLOR,
  height: 18,
  width: 18,
};

export const inModalSectionCard = (isMobile: boolean, width?: number): CSSProperties => ({
  marginTop: 12,
  marginBottom: 12,
  padding: isMobile ? 12 : 24,
  background: StyleConstants.WHITE_COLOR,
  border: '1px solid rgba(36, 55, 75, 0.2)',
  borderRadius: 8,
  width: isMobile ? '100%' : width ?? '100%',
});

export const label: CSSProperties = {
  marginTop: 10,
  fontSize: StyleConstants.SECONDARY_FONT_SIZE,
  fontWeight: StyleConstants.CARD_BODY_FONT_WEIGHT,
  color: StyleConstants.GREY_COLOR,
};

export const labelError: CSSProperties = {
  fontSize: StyleConstants.TERTIARY_FONT_SIZE,
  color: StyleConstants.ORANGE_COLOR,
};

export const checkbox: CSSProperties = {
  marginTop: 8,
  marginBottom: 8,
  fontSize: StyleConstants.TERTIARY_FONT_SIZE,
  fontWeight: StyleConstants.CARD_BODY_FONT_WEIGHT,
  color: StyleConstants.GREY_COLOR,
};

export const visibility: CSSProperties = {
  fontSize: StyleConstants.SECONDARY_FONT_SIZE,
  fontWeight: StyleConstants.CARD_BODY_FONT_WEIGHT,
  color: StyleConstants.GREY_COLOR,
};

export const columnHeader = (isMobile: boolean, marginBottom?: number): CSSProperties => ({
  width: '100%',
  wordBreak: 'break-word',
  textAlign: isMobile ? 'center' : undefined,
  fontSize: StyleConstants.HEADER_FONT_SIZE,
  fontWeight: StyleConstants.HEADER_FONT_WEIGHT,
  fontFamily: StyleConstants.HEADER_FONT_FAMILY,
  color: StyleConstants.GREY_COLOR,
  marginBottom,
});

export const button = (isMobile: boolean): CSSProperties => ({
  marginTop: isMobile ? 24 : undefined,
  marginBottom: isMobile ? 12 : undefined,
});

export const cornerButton = (isMobile = false): CSSProperties => ({
  marginLeft: isMobile ? 12 : 56,
  marginRight: isMobile ? 12 : 56,
  marginTop: isMobile ? 78 : 25,
});

export const emptyListMessage: CSSProperties = {
  wordBreak: 'break-word',
  lineBreak: 'strict',
  fontSize: StyleConstants.CARD_BODY_FONT_SIZE,
  fontWeight: StyleConstants.CARD_BODY_FONT_WEIGHT,
  fontFamily: StyleConstants.PRIMARY_FONT_FAMILY,
  color: StyleConstants.GREY_COLOR,
};

export const gridRow: CSSProperties = {
  marginTop: 12,
};

export const rowItem = (isLastItem = false): CSSProperties => ({
  marginBottom: isLastItem ? 0 : 12,
  width: '100%',
});

export const horizontalDivider: CSSProperties = {
  opacity: 0.5,
  height: 1,
  backgroundColor: 'rgba(36, 55, 75, 0.2)',
  marginBottom: 10,
  marginTop: 5,
};

export const error: CSSProperties = {
  color: StyleConstants.PINK_COLOR,
  marginTop: 12,
};

export const saveMessage: CSSProperties = {
  color: StyleConstants.PINK_COLOR,
  fontSize: 12,
};

export const addIcon: CSSProperties = {
  marginLeft: 8,
  color: StyleConstants.ORANGE_COLOR,
  minWidth: 25,
  minHeight: 25,
  cursor: 'pointer',
};

export const deleteButton: CSSProperties = {
  marginLeft: 12,
  marginTop: 28,
  marginBottom: 28,
  width: 150,
  cursor: 'pointer',
  wordBreak: 'break-word',
  fontSize: StyleConstants.CARD_BODY_FONT_SIZE,
  fontWeight: StyleConstants.CARD_BODY_FONT_WEIGHT,
  fontFamily: StyleConstants.PRIMARY_FONT_FAMILY,
  color: StyleConstants.PINK_COLOR,
};

export const deleteIcon: CSSProperties = {
  marginLeft: 8,
  height: 24,
  width: 24,
};

export const timeInput: CSSProperties = {
  marginLeft: 8,
};

export const roomWarning: CSSProperties = {
  color: StyleConstants.PINK_COLOR,
  fontSize: 12,
  marginTop: 12,
  fontWeight: 400,
};

export const maxHeight: CSSProperties = {
  height: '100%',
};

export const switchBoxShadow = '0px 1px 5px rgba(0, 0, 0, 0.6)';
export const switchActiveBoxShadow = '0px 0px 1px 10px rgba(0, 0, 0, 0.2)';
export const switchHeight = 15;
export const switchWidth = 34;
export const switchDiameter = 20;

export const modalText: CSSProperties = {
  fontSize: StyleConstants.SECONDARY_FONT_SIZE,
  fontWeight: StyleConstants.CARD_BODY_FONT_WEIGHT,
  color: StyleConstants.GREY_COLOR,
};
